// components/modals/ErrorRecordsModal.js
import React, { useState } from "react";
import { exportToExcel } from "../../utils/jobUtils";

const ErrorRecordsModal = ({ show, errorRecords, jobName, onClose }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");

  if (!show) return null;

  // Filter records based on search term
  const filteredRecords = errorRecords.filter((record) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      (record["เลขที่ใบกำกับ"] || "").toLowerCase().includes(searchLower) ||
      (record["Invoice No"] || "").toLowerCase().includes(searchLower) ||
      (record["รหัสลูกค้า"] || "").toLowerCase().includes(searchLower) ||
      (record["Outlet Code"] || "").toLowerCase().includes(searchLower) ||
      (record["Outlet Name"] || "").toLowerCase().includes(searchLower) ||
      (record["Remark"] || "").toLowerCase().includes(searchLower)
    );
  });

  // Sort records
  const sortedRecords = [...filteredRecords].sort((a, b) => {
    if (!sortField) return 0;

    const aValue = a[sortField] || "";
    const bValue = b[sortField] || "";

    if (sortDirection === "asc") {
      return aValue.toString().localeCompare(bValue.toString());
    } else {
      return bValue.toString().localeCompare(aValue.toString());
    }
  });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const handleExport = () => {
    try {
      const exportData = sortedRecords.map((record, index) => ({
        ลำดับ: index + 1,
        เลขที่ใบกำกับ: record["เลขที่ใบกำกับ"] || "",
        "Invoice No": record["Invoice No"] || "",
        วันที่: record["วันที่"] || "",
        "Calendar Day": record["Calendar Day"] || "",
        รหัสลูกค้า: record["รหัสลูกค้า"] || "",
        "Outlet Code": record["Outlet Code"] || "",
        "Outlet Name": record["Outlet Name"] || "",
        เลขที่บัญชี: record["เลขที่บัญชี"] || "",
        "Sales Route": record["Sales Route"] || "",
        "ค่าสินค้า/บริการ": parseFloat(record["ค่าสินค้า/บริการ"]) || 0,
        NIV: parseFloat(record["NIV"]) || 0,
        "NIV Diff": parseFloat(record["NIV Diff"]) || 0,
        ภาษีมูลค่าเพิ่ม: parseFloat(record["ภาษีมูลค่าเพิ่ม"]) || 0,
        VAT: parseFloat(record["VAT"]) || 0,
        "VAT Diff": parseFloat(record["VAT Diff"]) || 0,
        ข้อผิดพลาด: record["Remark"] || "",
      }));

      const filename = `${jobName}_errors_${sortedRecords.length}_records_${
        new Date().toISOString().split("T")[0]
      }.xlsx`;

      exportToExcel(exportData, filename, "Error Records");
    } catch (error) {
      console.error("Export failed:", error);
      alert("ไม่สามารถส่งออกได้ กรุณาลองใหม่อีกครั้ง");
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return "↕️";
    return sortDirection === "asc" ? "↑" : "↓";
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal"
        style={{ maxWidth: "1200px", width: "95%" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2 style={{ fontSize: "20px", fontWeight: "600" }}>
            📋 ข้อผิดพลาดในการเปรียบเทียบ - {jobName}
          </h2>
          <button className="modal-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="modal-body">
          {/* Summary Alert */}
          <div style={{ marginBottom: "20px" }}>
            <div
              style={{
                background: "#fef3c7",
                border: "1px solid #f59e0b",
                borderRadius: "8px",
                padding: "16px",
                display: "flex",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <span style={{ fontSize: "20px" }}>⚠️</span>
              <div>
                <strong>
                  พบข้อผิดพลาด {errorRecords.length} รายการ
                  {filteredRecords.length !== errorRecords.length &&
                    ` (แสดง ${filteredRecords.length} รายการจากการค้นหา)`}
                </strong>
                <br />
                <small style={{ color: "#92400e" }}>
                  แสดงเฉพาะรายการที่มีข้อผิดพลาดในการเปรียบเทียบ
                </small>
              </div>
            </div>
          </div>

          {/* Search and Filter Controls */}
          <SearchAndFilterControls
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            recordCount={filteredRecords.length}
            totalCount={errorRecords.length}
            onExport={handleExport}
          />

          {/* Error Records Table */}
          <div
            className="table-container"
            style={{
              maxHeight: "60vh",
              overflowY: "auto",
              overflowX: "auto",
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
            }}
          >
            <table
              className="table"
              style={{ marginBottom: "0", minWidth: "1400px" }}
            >
              <thead
                style={{
                  position: "sticky",
                  top: "0",
                  backgroundColor: "#fef2f2",
                  zIndex: "10",
                }}
              >
                <tr>
                  <SortableHeader
                    field="เลขที่ใบกำกับ"
                    onSort={handleSort}
                    sortIcon={getSortIcon("เลขที่ใบกำกับ")}
                    style={{ minWidth: "120px" }}
                  >
                    เลขที่ใบกำกับ
                  </SortableHeader>
                  <SortableHeader
                    field="Invoice No"
                    onSort={handleSort}
                    sortIcon={getSortIcon("Invoice No")}
                    style={{ minWidth: "120px" }}
                  >
                    Invoice No
                  </SortableHeader>
                  <SortableHeader
                    field="รหัสลูกค้า"
                    onSort={handleSort}
                    sortIcon={getSortIcon("รหัสลูกค้า")}
                    style={{ minWidth: "100px" }}
                  >
                    รหัสลูกค้า
                  </SortableHeader>
                  <SortableHeader
                    field="Outlet Code"
                    onSort={handleSort}
                    sortIcon={getSortIcon("Outlet Code")}
                    style={{ minWidth: "120px" }}
                  >
                    Outlet Code
                  </SortableHeader>
                  <SortableHeader
                    field="Outlet Name"
                    onSort={handleSort}
                    sortIcon={getSortIcon("Outlet Name")}
                    style={{ minWidth: "150px" }}
                  >
                    Outlet Name
                  </SortableHeader>
                  <th style={{ minWidth: "100px" }}>เลขที่บัญชี</th>
                  <th style={{ minWidth: "100px" }}>Sales Route</th>
                  <th style={{ minWidth: "120px", textAlign: "right" }}>
                    ค่าสินค้า/บริการ
                  </th>
                  <th style={{ minWidth: "100px", textAlign: "right" }}>NIV</th>
                  <th style={{ minWidth: "120px", textAlign: "right" }}>
                    ภาษีมูลค่าเพิ่ม
                  </th>
                  <th style={{ minWidth: "100px", textAlign: "right" }}>VAT</th>
                  <SortableHeader
                    field="Remark"
                    onSort={handleSort}
                    sortIcon={getSortIcon("Remark")}
                    style={{ minWidth: "250px" }}
                  >
                    🚨 ข้อผิดพลาด
                  </SortableHeader>
                </tr>
              </thead>
              <tbody>
                {sortedRecords.length > 0 ? (
                  sortedRecords.map((record, index) => (
                    <ErrorRecordRow key={index} record={record} />
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="12"
                      style={{ textAlign: "center", padding: "40px" }}
                    >
                      {searchTerm
                        ? "ไม่พบข้อมูลที่ตรงกับการค้นหา"
                        : "ไม่มีข้อผิดพลาด"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Summary Section */}
          <ErrorSummary
            totalErrors={errorRecords.length}
            filteredErrors={filteredRecords.length}
            onExport={handleExport}
            jobName={jobName}
          />
        </div>

        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>
            ปิด
          </button>
        </div>
      </div>
    </div>
  );
};

// Sub-components
const SearchAndFilterControls = ({
  searchTerm,
  onSearchChange,
  recordCount,
  totalCount,
  onExport,
}) => (
  <div
    style={{
      marginBottom: "20px",
      padding: "16px",
      background: "#f8fafc",
      borderRadius: "8px",
      border: "1px solid #e2e8f0",
    }}
  >
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "16px",
        flexWrap: "wrap",
      }}
    >
      <div style={{ flex: "1", minWidth: "200px" }}>
        <input
          type="text"
          placeholder="🔍 ค้นหาเลขที่ใบกำกับ, Invoice No, รหัสลูกค้า, ข้อผิดพลาด..."
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          style={{
            width: "100%",
            padding: "8px 12px",
            border: "1px solid #d1d5db",
            borderRadius: "6px",
            fontSize: "14px",
          }}
        />
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <span style={{ fontSize: "14px", color: "#64748b" }}>
          แสดง {recordCount.toLocaleString()} จาก {totalCount.toLocaleString()}{" "}
          รายการ
        </span>
        <button className="btn btn-success btn-sm" onClick={onExport}>
          📥 ส่งออก Excel
        </button>
      </div>
    </div>
  </div>
);

const SortableHeader = ({ field, onSort, sortIcon, children, style }) => (
  <th
    style={{
      ...style,
      cursor: "pointer",
      userSelect: "none",
      position: "relative",
    }}
    onClick={() => onSort(field)}
    title={`คลิกเพื่อเรียงลำดับตาม ${children}`}
  >
    <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
      {children}
      <span style={{ fontSize: "12px", opacity: "0.7" }}>{sortIcon}</span>
    </div>
  </th>
);

const ErrorRecordRow = ({ record }) => (
  <tr style={{ backgroundColor: "#fef2f2" }}>
    <td
      style={{
        fontWeight: "600",
        fontFamily: "monospace",
        color: "#dc2626",
      }}
    >
      {record["เลขที่ใบกำกับ"] || "-"}
    </td>
    <td
      style={{
        fontFamily: "monospace",
        color: record["Invoice No"] ? "#059669" : "#dc2626",
      }}
    >
      {record["Invoice No"] || "-"}
    </td>
    <td style={{ fontFamily: "monospace", fontSize: "13px" }}>
      {record["รหัสลูกค้า"] || "-"}
    </td>
    <td style={{ fontFamily: "monospace", fontSize: "13px" }}>
      {record["Outlet Code"] || "-"}
    </td>
    <td style={{ fontSize: "13px" }}>{record["Outlet Name"] || "-"}</td>
    <td style={{ fontFamily: "monospace", fontSize: "13px" }}>
      {record["เลขที่บัญชี"] || "-"}
    </td>
    <td style={{ fontFamily: "monospace", fontSize: "13px" }}>
      {record["Sales Route"] || "-"}
    </td>
    <td
      style={{
        fontWeight: "600",
        textAlign: "right",
        fontFamily: "monospace",
        fontSize: "13px",
      }}
    >
      {(parseFloat(record["ค่าสินค้า/บริการ"]) || 0).toLocaleString()}
    </td>
    <td
      style={{
        fontWeight: "600",
        textAlign: "right",
        fontFamily: "monospace",
        fontSize: "13px",
      }}
    >
      {(parseFloat(record["NIV"]) || 0).toLocaleString()}
    </td>
    <td
      style={{
        fontWeight: "600",
        textAlign: "right",
        fontFamily: "monospace",
        fontSize: "13px",
      }}
    >
      {(parseFloat(record["ภาษีมูลค่าเพิ่ม"]) || 0).toLocaleString()}
    </td>
    <td
      style={{
        fontWeight: "600",
        textAlign: "right",
        fontFamily: "monospace",
        fontSize: "13px",
      }}
    >
      {(parseFloat(record["VAT"]) || 0).toLocaleString()}
    </td>
    <td
      style={{
        fontSize: "12px",
        color: "#dc2626",
        fontWeight: "600",
        backgroundColor: "#fef2f2",
        padding: "8px",
        borderLeft: "3px solid #dc2626",
      }}
    >
      {record["Remark"] || "-"}
    </td>
  </tr>
);

const ErrorSummary = ({ totalErrors, filteredErrors, onExport, jobName }) => (
  <div
    style={{
      marginTop: "20px",
      padding: "16px",
      background: "#f8fafc",
      borderRadius: "8px",
      border: "1px solid #e2e8f0",
    }}
  >
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <div>
        <strong>สรุปข้อผิดพลาด:</strong>
        <ul
          style={{
            margin: "8px 0 0 20px",
            fontSize: "14px",
            color: "#64748b",
          }}
        >
          <li>
            📊 รายการที่มีข้อผิดพลาด: <strong>{totalErrors}</strong> รายการ
          </li>
          {filteredErrors !== totalErrors && (
            <li>
              🔍 รายการที่แสดง: <strong>{filteredErrors}</strong> รายการ
            </li>
          )}
          <li>💡 แนะนำ: ตรวจสอบข้อมูลต้นทางก่อนการเปรียบเทียบใหม่</li>
        </ul>
      </div>
      <button className="btn btn-success" onClick={onExport}>
        📥 ส่งออกข้อผิดพลาด Excel
      </button>
    </div>
  </div>
);

export default ErrorRecordsModal;
